import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api, type ApiListResponse } from '@/lib/api';
import type { Preset } from '@/types/presets';
import { cn } from '@/lib/utils';
import { Plus, Pencil, Trash2, RefreshCw } from 'lucide-react';

function ScopeBadge({ preset }: { preset: Preset }) {
    if (preset.user) return <Badge variant="default">User</Badge>;
    if (preset.role) return <Badge variant="secondary">Role</Badge>;
    return <Badge variant="outline">Global</Badge>;
}

function PresetRow({ preset, onDelete }: { preset: Preset; onDelete: (id: number) => void }) {
    const navigate = useNavigate();
    const userDisplay = (preset as any)?.user?.email || (preset as any)?.user?.id || null;
    const roleDisplay = (preset as any)?.role?.name || (preset as any)?.role?.id || null;
    const scopeDetail = userDisplay || roleDisplay || 'Global';

    return (
        <div className="grid grid-cols-12 items-center border-b px-3 py-3 text-sm last:border-b-0">
            <div className="col-span-3 font-medium truncate">{preset.bookmark || 'Default Preset'}</div>
            <div className="col-span-2 truncate">{preset.collection || '—'}</div>
            <div className="col-span-2 flex items-center gap-2">
                <ScopeBadge preset={preset} />
                <span className="text-muted-foreground truncate">{scopeDetail}</span>
            </div>
            <div className="col-span-2 truncate">{preset.layout || '—'}</div>
            <div className="col-span-2 truncate">{preset.search || '—'}</div>
            <div className="col-span-1 flex justify-end gap-2">
                <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/settings/presets/${preset.id}`)}>
                    <Pencil className="h-4 w-4" />
                </Button>
                {preset.id && (
                    <Button variant="ghost" size="icon" onClick={() => onDelete(preset.id!)} className="text-destructive">
                        <Trash2 className="h-4 w-4" />
                    </Button>
                )}
            </div>
        </div>
    );
}

export function PresetListPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [confirmId, setConfirmId] = useState<number | null>(null);

    const { data, isFetching } = useQuery({
        queryKey: ['settings-presets'],
        queryFn: async () => {
            const qs = [
                'limit=25',
                'page=1',
                'sort[]=id',
                'fields[]=bookmark',
                'fields[]=collection',
                'fields[]=user.email',
                'fields[]=user.id',
                'fields[]=role.name',
                'fields[]=role.id',
                'fields[]=id',
            ].join('&');
            const response = await api.get<ApiListResponse<Preset>>(`/presets?${qs}`);
            return response.data || [];
        },
    });

    const sortedPresets = useMemo(() => {
        const list = data || [];
        return [...list].sort((a, b) => {
            const aName = a.bookmark || a.collection || '';
            const bName = b.bookmark || b.collection || '';
            return aName.localeCompare(bName);
        });
    }, [data]);

    const handleDelete = async (id: number) => {
        setConfirmId(null);
        await api.delete('/presets', [id]);
        await queryClient.invalidateQueries({ queryKey: ['settings-presets'] });
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold">Presets</h2>
                    <p className="text-sm text-muted-foreground">Manage saved layouts, filters, and bookmarks.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" onClick={() => queryClient.invalidateQueries({ queryKey: ['settings-presets'] })} disabled={isFetching}>
                        <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
                    </Button>
                    <Button onClick={() => navigate('/admin/settings/presets/+')}>
                        <Plus className="h-4 w-4 mr-2" />
                        New Preset
                    </Button>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">All Presets</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {sortedPresets.length === 0 ? (
                        <div className="p-6 text-sm text-muted-foreground">
                            No presets yet. Create one to save layouts or bookmarks.
                        </div>
                    ) : (
                        <div className="divide-y">
                            <div className="grid grid-cols-12 px-3 py-2 text-xs uppercase text-muted-foreground">
                                <span className="col-span-3">Name / Bookmark</span>
                                <span className="col-span-2">Collection</span>
                                <span className="col-span-2">Scope</span>
                                <span className="col-span-2">Layout</span>
                                <span className="col-span-2">Search</span>
                                <span className="col-span-1 text-right">Actions</span>
                            </div>
                            {sortedPresets.map((preset) => (
                                <PresetRow key={preset.id || `${preset.collection}-${preset.bookmark}`} preset={preset} onDelete={(id) => setConfirmId(id)} />
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <AlertDialog open={confirmId !== null} onOpenChange={(open) => !open && setConfirmId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete preset?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. The preset will be permanently removed.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => confirmId && handleDelete(confirmId)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
